"use client";

import { Button } from "@/components/ui/button";
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
} from "@/components/ui/drawer";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
    NavigationMenu,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { ChevronRight, Menu } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useState } from "react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger, } from "@/components/ui/collapsible";
import { cva } from "class-variance-authority";

const navigationMenuTriggerStyle = cva(
    "group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:text-jaune focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50"
);

interface MenuItemTypeChild {
    label: string;
    description?: string;
    href: string;
    children?: MenuItemTypeChild[];
}

type MenuCustomItem = React.ReactNode;


interface OriginalMenuItemType {
    gridColumns?: number;
    label: string;
    href: string;
    children?: MenuItemTypeChild[];
}

type MenuItemType = OriginalMenuItemType | MenuCustomItem;


const menuItems: MenuItemType[] = [
    {
        label: "L'Office du Tourisme",
        href: "/espace-pro/office-de-tourisme",
    },
    {
        label: "Pôle de l'Observation et des Etudes",
        href: "/espace-pro/pole-observation-etudes",
    },
    {
        label: "L'Office et Vous",
        href: "/espace-pro/office-et-vous",
    },
    {
        label: "Devenir Partenaire",
        href: "/espace-pro/devenir-partenaire",
    },
    {
        label: "Promotion",
        href: "/espace-pro/promotion",
    }
];


function isActivePath(path: string | null, href: string) {
    if (!path)
        return false;
    return path === href || path.startsWith(href);
}

function WebMenuDeroulant({item}: { item: OriginalMenuItemType }) {
    const [isOpen, setIsOpen] = useState(false);
    const { pathname: pathName } = useLocation();

    return <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <NavigationMenuItem>
            <DropdownMenuTrigger asChild onMouseEnter={() => setIsOpen(true)}
                                 onMouseLeave={() => setIsOpen(false)}>
                <NavigationMenuTrigger className={cn({
                    "text-accent bg-bleu-marine": isActivePath(pathName, item.href)
                })}>{item.label}</NavigationMenuTrigger>
            </DropdownMenuTrigger>
            <DropdownMenuContent onMouseEnter={() => setIsOpen(true)}
                                 onMouseLeave={() => setIsOpen(false)}>
                {(item.children as MenuItemTypeChild[]).map((child, index) => (
                    <div key={index}>
                        {child.children ? (<>
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger className={cn({
                                        "text-accent bg-bleu-marine": isActivePath(pathName, child.href)
                                    })}>
                                        {child.label}
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                        {child.children.map((subChild, index) => (
                                            <DropdownMenuItem key={index}>
                                                <Link to={subChild.href}>
                                                    <NavigationMenuLink>{subChild.label}</NavigationMenuLink>
                                                </Link>
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>
                            </>)
                            : (<>
                                <DropdownMenuItem key={index} className={cn({
                                    "text-accent bg-bleu-marine": isActivePath(pathName, child.href)
                                })}>
                                    <Link to={child.href}>
                                        <NavigationMenuLink>{child.label}</NavigationMenuLink>
                                    </Link>
                                </DropdownMenuItem>
                            </>)}
                    </div>
                ))}
            </DropdownMenuContent>
        </NavigationMenuItem>
    </DropdownMenu>;
}


function MobileMenuDeroulant({item, handleClose}: { item: OriginalMenuItemType, handleClose: () => any }) {
    const [isOpen, setIsOpen] = useState(false);
    const { pathname: pathName } = useLocation();

    return <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className={"w-full border-b border-slate-200"}>
            <div className={cn(navigationMenuTriggerStyle(), "w-full flex justify-between", {
                "text-accent bg-bleu-marine": isActivePath(pathName, item.href)
            })}>
                {item.label}

                <ChevronRight
                    className={cn("h-4 w-4", {
                        "rotate-90": isOpen
                    })}/>
            </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
            {(item.children as MenuItemTypeChild[]).map((child, index) => (
                <div key={index}>
                    {child.children ? (<>
                            <MobileMenuDeroulant item={child as OriginalMenuItemType} handleClose={handleClose}/>
                        </>)
                        : (<>
                            <Link to={(child as OriginalMenuItemType).href}>
                                <div
                                    className={cn(navigationMenuTriggerStyle(), "w-full justify-start border-b border-slate-200 cursor-pointer", {
                                        "text-accent bg-bleu-marine": isActivePath(pathName, (child as OriginalMenuItemType).href)
                                    })} onClick={handleClose}>
                                    <div>
                                        {(child as OriginalMenuItemType).label}
                                    </div>
                                </div>
                            </Link>
                        </>)}
                </div>
            ))}
        </CollapsibleContent>
    </Collapsible>;
}


export default function MenuPrincipal() {
    const [isOpen, setIsOpen] = useState(false);
    const { pathname: pathName } = useLocation();

    return (
        <>
            {/*<AppMenu menuItems={menuItems} mobileTitle="Menu" mobileDescription="Navigation principale" />*/}
            {/*Web menu*/}
            <div className={"hidden xl:block"}>
                <NavigationMenu>
                    <NavigationMenuList className={"gap-8"}>
                        {menuItems.map((item, index) => <div key={index}>
                            {((item as OriginalMenuItemType).label) ? (<>
                                    {(item as OriginalMenuItemType).children?.length !== undefined ? (<>
                                            <WebMenuDeroulant item={item as OriginalMenuItemType}/>
                                        </>)
                                        : (<>
                                            <Link to={(item as OriginalMenuItemType).href}>
                                                <NavigationMenuLink className={cn(navigationMenuTriggerStyle(),
                                                    "text-white bg-transparent font-bold",
                                                    {
                                                    // "text-accent bg-bleu-marine": isActivePath(pathName, (item as OriginalMenuItemType).href)
                                                })}>
                                                    {(item as OriginalMenuItemType).label}
                                                </NavigationMenuLink>
                                            </Link>
                                        </>)}
                                </>)
                                : (<>
                                    {(item as MenuCustomItem)}
                                </>)}
                        </div>)}
                    </NavigationMenuList>
                </NavigationMenu>
            </div>

            {/*Mobile menu*/}
            <div className={"block xl:hidden"}>
                <button
                    className="block xl:hidden p-4"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <Menu className={"text-gray-400"}/>
                </button>

                <Drawer open={isOpen} onOpenChange={setIsOpen}>
                    <DrawerContent>
                        <DrawerHeader>
                            <DrawerTitle>Menu</DrawerTitle>
                            <DrawerDescription>Navigation principale</DrawerDescription>
                        </DrawerHeader>
                        <div className={"flex flex-col"}>
                            {menuItems.map((item, index) => {
                                return ((item as OriginalMenuItemType).label) ? (<div key={index}>
                                        {(item as OriginalMenuItemType).children?.length !== undefined ? (<>
                                                <MobileMenuDeroulant item={item as OriginalMenuItemType} handleClose={() => setIsOpen(false)}/>
                                            </>)
                                            : (<>
                                                <Link to={(item as OriginalMenuItemType).href}>
                                                    <div
                                                        className={cn(navigationMenuTriggerStyle(), "w-full justify-start border-b border-slate-200 cursor-pointer", {
                                                            "text-accent bg-bleu-marine": isActivePath(pathName, (item as OriginalMenuItemType).href)
                                                        })} onClick={() => setIsOpen(false)}>
                                                        <div>
                                                            {(item as OriginalMenuItemType).label}
                                                        </div>
                                                    </div>
                                                </Link>
                                            </>)}
                                    </div>)
                                    : (<div className={"flex justify-center"}>
                                        {(item as MenuCustomItem)}
                                    </div>);
                            })}
                        </div>
                        <DrawerFooter>
                            <DrawerClose asChild>
                                <Button variant={"outline"} className="w-full p-4">Fermer</Button>
                            </DrawerClose>
                        </DrawerFooter>
                    </DrawerContent>
                </Drawer>
            </div>


        </>
    );
}   
